import { FAQItem } from '../components/FAQ'

export type ServicePageData = {
  slug: string
  h1: string
  metaLead: string
  heroImage: string
  ctaLabel: string
  forWho: string
  situations: string[]
  beforeYouAct: { title: string; body: string }[]
  howWeHelp: string[]
  process: { title: string; body: string }[]
  team: { name: string; role: string; blurb: string }[]
  faqs: FAQItem[]
  guides: string[]
  related: { label: string; slug: string }[]
  localNote: string
}

const commercialTeam = [
  {
    name: 'Commercial Team',
    role: 'Business & Commercial Law',
    blurb:
      'Senior commercial lawyers who translate legal risk into practical business decisions.',
  },
  {
    name: 'Transactions Team',
    role: 'Sales, Structures & Agreements',
    blurb:
      'Focused on getting deals documented clearly, negotiated well and completed on time.',
  },
]

export const COMMERCIAL: ServicePageData = {
  slug: 'commercial-business-law',
  h1: 'Commercial & Business Lawyers Perth',
  metaLead:
    'Practical commercial legal advice for WA business owners — contracts, structures, agreements, sales and succession, explained clearly before you commit.',
  heroImage:
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80',
  ctaLabel: 'Get commercial legal advice',
  forWho:
    "This service is for WA business owners, directors, partners and investors who need clear, commercial legal advice — whether you're starting out, growing, buying, selling, restructuring or protecting what you've built. We focus on the decision in front of you and the risk you may not be able to see.",
  situations: [
    'Business structures',
    'Contracts',
    'Shareholder, partnership and JV agreements',
    'Business sale and purchase',
    'Franchise agreements',
    'Business succession',
    'Independent advice for guarantors',
    'Loan and finance documents',
    'Terms of trade',
    'Privacy and compliance',
  ],
  beforeYouAct: [
    {
      title: 'Before you sign',
      body: 'Have the agreement reviewed so you understand your obligations, exit rights and personal exposure — especially any guarantees.',
    },
    {
      title: 'Before you sell or buy',
      body: 'Get advice on structure, warranties and the sale terms early. The framing of a deal is often more important than the price.',
    },
    {
      title: 'Before you restructure',
      body: 'Understand the tax, control and succession implications before moving assets or changing ownership.',
    },
  ],
  howWeHelp: [
    'We start with the transaction or risk, not the paperwork — so advice is grounded in your commercial goal.',
    'We review and explain documents in plain English, flagging what genuinely matters.',
    'We identify legal and commercial exposure and rank it, so you know what to prioritise.',
    'We give you clear options, likely outcomes and the costs involved before you decide.',
    'We draft, negotiate or finalise — and stay proportionate to the value at stake.',
  ],
  process: [
    {
      title: 'Understand the transaction or risk',
      body: "We take the time to understand your commercial objective and what's driving the matter.",
    },
    {
      title: 'Review documents',
      body: 'We work through the contracts, agreements and supporting documents in detail.',
    },
    {
      title: 'Identify legal and commercial exposure',
      body: 'We highlight the real risks — and separate the critical issues from the noise.',
    },
    {
      title: 'Provide clear options',
      body: 'You receive practical recommendations, likely outcomes and a clear view on costs.',
    },
    {
      title: 'Draft, negotiate or finalise',
      body: 'We document, negotiate or complete the matter to protect your position.',
    },
  ],
  team: commercialTeam,
  faqs: [
    {
      q: 'Can you review a contract before I sign it?',
      a: 'Yes — this is one of the most valuable things we do. We identify the legal and commercial risks in a contract and explain them clearly so you can decide with confidence.',
    },
    {
      q: 'Do I need to be a large business to work with you?',
      a: 'No. We act for sole traders, small and medium businesses, family enterprises and larger companies across Western Australia.',
    },
    {
      q: 'Can you help me choose the right business structure?',
      a: 'Yes. We advise on company, trust, partnership and sole-trader structures with regard to liability, tax and succession, working alongside your accountant where needed.',
    },
    {
      q: 'What is independent advice for guarantors?',
      a: 'Lenders often require guarantors to obtain independent legal advice before signing. We explain exactly what you are agreeing to and the personal risk involved, and provide the required certificate.',
    },
    {
      q: 'Do you offer fixed fees for commercial work?',
      a: 'For many defined tasks — such as contract reviews or standard agreements — we can offer fixed or capped fees, and we always confirm how we charge before starting.',
    },
  ],
  guides: [
    'WA Business Owner Legal Health Check',
    'Commercial Lease Review Checklist',
    'Business Succession Planning Guide',
    'Commercial Dispute Response Guide',
  ],
  related: [
    { label: 'Dispute Resolution & Litigation', slug: 'dispute-resolution' },
    { label: 'Franchising Law', slug: 'franchising' },
    { label: 'Employment & Workplace Relations', slug: 'employment' },
    { label: 'Property Law', slug: 'property-law' },
  ],
  localNote:
    'Based in Nedlands and acting for businesses across Perth and Western Australia, we understand the local commercial landscape — from small business owners in the western suburbs to established WA enterprises.',
}

// Lightweight data for the other services so every page is unique & specific.
export const SERVICE_PAGES: Record<string, ServicePageData> = {
  'commercial-business-law': COMMERCIAL,

  'dispute-resolution': {
    slug: 'dispute-resolution',
    h1: 'Dispute Resolution & Litigation Lawyers Perth',
    metaLead:
      'Strategic, proportionate dispute resolution for WA businesses and individuals — resolving commercial, property, employment and estate disputes without unnecessary cost.',
    heroImage:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Get advice on your dispute',
    forWho:
      'This service is for anyone facing a dispute — a demand, a claim, a breach or a breakdown in a business or personal relationship. We help you understand your position early and choose a strategy that fits the value and the outcome you want.',
    situations: [
      'A demand or claim received',
      'Contract or commercial disputes',
      'Shareholder & partnership disputes',
      'Property & building disputes',
      'Employment disputes',
      'Estate & inheritance disputes',
      'Professional negligence claims',
      'Debt recovery',
    ],
    beforeYouAct: [
      {
        title: 'Before you respond',
        body: "Don't reply to a demand or claim in anger or in writing without advice — early words can affect the whole dispute.",
      },
      {
        title: 'Before it escalates',
        body: 'Most disputes are cheaper to resolve early. A measured, well-advised response often prevents litigation.',
      },
      {
        title: 'Before you commit to court',
        body: 'We assess the realistic cost, time and prospects so you can weigh litigation against negotiation or mediation.',
      },
    ],
    howWeHelp: [
      'We assess the merits, risks and realistic outcomes of your dispute early.',
      "We resolve matters through negotiation and mediation wherever it's appropriate and proportionate.",
      'We keep strategy tied to the value at stake — no fighting for the sake of it.',
      'We advocate firmly and clearly when litigation is the right path.',
      'We keep you informed on likely cost and progress at every stage.',
    ],
    process: [
      {
        title: 'Understand what has happened',
        body: 'We map the facts, documents and relationships behind the dispute.',
      },
      {
        title: 'Assess merits and risk',
        body: 'We give you a candid view of your position and likely outcomes.',
      },
      {
        title: 'Set a proportionate strategy',
        body: 'We agree an approach that fits the value, urgency and your goals.',
      },
      {
        title: 'Negotiate or mediate',
        body: 'We pursue a commercial resolution wherever it makes sense.',
      },
      {
        title: 'Litigate if necessary',
        body: 'If the matter needs to be decided, we advocate for your position.',
      },
    ],
    team: [
      {
        name: 'Litigation Team',
        role: 'Dispute Resolution & Litigation',
        blurb:
          'Experienced litigators focused on resolving disputes efficiently and proportionately.',
      },
    ],
    faqs: [
      {
        q: 'Do disputes always end up in court?',
        a: "No. Most disputes are resolved through negotiation or mediation. Court is one option, chosen only when it's the right and proportionate path.",
      },
      {
        q: "I've received a demand — what should I do?",
        a: "Don't respond in writing before getting advice. Contact us promptly so we can protect your position and shape the response.",
      },
      {
        q: 'How much will a dispute cost?',
        a: "It depends on complexity and how the other side behaves. We give you an early, realistic view and keep strategy proportionate to what's at stake.",
      },
      {
        q: 'Can you help with estate or inheritance disputes?',
        a: 'Yes. We act in estate and inheritance disputes, including claims against estates, alongside our wills and estates team.',
      },
    ],
    guides: [
      'Commercial Dispute Response Guide',
      'WA Business Owner Legal Health Check',
    ],
    related: [
      { label: 'Commercial & Business Law', slug: 'commercial-business-law' },
      { label: 'Employment & Workplace Relations', slug: 'employment' },
      { label: 'Property Law', slug: 'property-law' },
      { label: 'Wills, Estates & Succession', slug: 'wills-estates' },
    ],
    localNote:
      'We appear in and advise on matters across WA courts and tribunals, with practical knowledge of local dispute processes.',
  },

  'property-law': {
    slug: 'property-law',
    h1: 'Property Lawyers Perth',
    metaLead:
      'Clear legal advice for residential, commercial and development property across WA — contracts, disputes, leasing and due diligence.',
    heroImage:
      'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Get property legal advice',
    forWho:
      'This service is for buyers, sellers, investors, landlords, tenants and developers who want a property matter handled properly — from a contract review to a complex development or dispute.',
    situations: [
      'Contract of sale review',
      'Residential property advice',
      'Commercial property',
      'Commercial & retail leasing',
      'Property & boundary disputes',
      'Development & subdivision',
      'Due diligence',
      'Caveats & title issues',
    ],
    beforeYouAct: [
      {
        title: 'Before you sign a contract',
        body: 'Have the contract and special conditions reviewed — small clauses can carry large consequences.',
      },
      {
        title: 'Before you settle',
        body: "Make sure finance, conditions and searches are properly aligned so settlement isn't delayed.",
      },
      {
        title: 'Before a development',
        body: 'Understand planning, title and due diligence issues before you commit capital.',
      },
    ],
    howWeHelp: [
      'We review contracts and special conditions and explain the risks clearly.',
      'We advise on commercial property, leasing and development matters.',
      'We work closely with our settlements team to keep transactions on track.',
      'We act firmly and practically in property disputes.',
      'We flag due diligence and title issues before they become costly.',
    ],
    process: [
      {
        title: 'Understand the property matter',
        body: 'We clarify your goal — buying, selling, leasing, developing or resolving a dispute.',
      },
      {
        title: 'Review documents & title',
        body: 'We examine the contract, title, searches and any conditions.',
      },
      {
        title: 'Identify risks',
        body: 'We highlight the legal and practical risks specific to your matter.',
      },
      {
        title: 'Advise on options',
        body: 'You get clear recommendations and likely outcomes.',
      },
      {
        title: 'Act or complete',
        body: 'We negotiate, document or resolve to protect your position.',
      },
    ],
    team: [
      {
        name: 'Property Team',
        role: 'Property Law',
        blurb:
          'Property lawyers advising across residential, commercial and development matters in WA.',
      },
    ],
    faqs: [
      {
        q: 'Should I get a contract reviewed before I sign?',
        a: 'Yes. A short review before signing can prevent significant problems later, especially around special conditions and finance clauses.',
      },
      {
        q: 'Do you handle commercial and residential property?',
        a: 'Yes — we advise across residential, commercial and development property matters throughout WA.',
      },
      {
        q: 'Can you help with a property or boundary dispute?',
        a: 'Yes. We advise on property, boundary and neighbour disputes and work with our litigation team where needed.',
      },
    ],
    guides: [
      'Property Settlement Checklist',
      'Commercial Lease Review Checklist',
    ],
    related: [
      { label: 'Conveyancing & Settlements', slug: 'conveyancing' },
      { label: 'Commercial & Business Law', slug: 'commercial-business-law' },
      { label: 'Dispute Resolution & Litigation', slug: 'dispute-resolution' },
    ],
    localNote:
      'From Nedlands and the western suburbs to development sites across greater Perth, we understand the WA property market and its legal context.',
  },

  conveyancing: {
    slug: 'conveyancing',
    h1: 'Conveyancing & Settlement Lawyers Perth',
    metaLead:
      'Premium conveyancing and settlement support for WA buyers, sellers, investors and developers — accurate, on-time and stress-free.',
    heroImage:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Get settlement support',
    forWho:
      'This service is for anyone buying or selling property in WA who wants their settlement handled accurately and on time — with a legal team behind it if anything goes wrong.',
    situations: [
      'Buying property',
      'Selling property',
      'Off-the-plan purchases',
      'Investment property',
      'Transfers between related parties',
      'Settlement of commercial property',
      'First home buyers',
      'Developer settlements',
    ],
    beforeYouAct: [
      {
        title: 'Before you make an offer',
        body: "Understand the deposit, conditions and settlement timing so you're not caught out.",
      },
      {
        title: 'Before finance approval',
        body: 'Align your finance clause and settlement date to avoid penalties or default.',
      },
      {
        title: 'Before settlement day',
        body: 'Ensure searches, adjustments and figures are correct well ahead of time.',
      },
    ],
    howWeHelp: [
      'We manage your settlement end-to-end with legal oversight throughout.',
      "We coordinate with your bank, agent and the other side's representatives.",
      'We check adjustments, searches and settlement figures carefully.',
      'We keep you updated so you always know where things stand.',
      'If a legal issue arises, our property lawyers step in immediately.',
    ],
    process: [
      {
        title: 'Open your matter',
        body: 'We confirm the contract details, dates and conditions.',
      },
      {
        title: 'Conduct searches',
        body: 'We order and review the necessary title and property searches.',
      },
      {
        title: 'Prepare documents',
        body: 'We prepare and check the transfer and settlement documents.',
      },
      {
        title: 'Coordinate settlement',
        body: 'We align all parties, finance and figures for settlement.',
      },
      {
        title: 'Settle & confirm',
        body: 'We complete settlement and confirm the outcome with you.',
      },
    ],
    team: [
      {
        name: 'Settlements Team',
        role: 'Conveyancing & Settlements',
        blurb:
          'Settlement specialists supported by qualified property lawyers.',
      },
    ],
    faqs: [
      {
        q: "What's the difference between a settlement agent and a law firm?",
        a: 'With Cullen Macleod your settlement is handled with full legal oversight — if a legal issue arises, property lawyers are already on your matter.',
      },
      {
        q: 'Can you handle off-the-plan purchases?',
        a: 'Yes. We regularly assist buyers with off-the-plan and developer settlements across WA.',
      },
      {
        q: 'When should I engage you?',
        a: 'As early as possible — ideally before you sign the contract, so we can review conditions and timing.',
      },
    ],
    guides: ['Property Settlement Checklist'],
    related: [
      { label: 'Property Law', slug: 'property-law' },
      { label: 'Commercial & Business Law', slug: 'commercial-business-law' },
    ],
    localNote:
      'We settle residential and commercial property across Perth and regional WA, with strong local knowledge of the settlement process.',
  },

  'family-law': {
    slug: 'family-law',
    h1: 'Family Lawyers Perth',
    metaLead:
      'Calm, clear family law advice in WA — divorce, parenting, property settlement, child support and binding financial agreements.',
    heroImage:
      'https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Speak with a family lawyer',
    forWho:
      'This service is for anyone navigating separation, divorce, parenting or a family property matter. We give calm, practical guidance and protect what matters most to you.',
    situations: [
      'Separation & divorce',
      'Parenting arrangements',
      'Property settlement',
      'Child support',
      'Binding financial agreements',
      'Mediation & negotiation',
      'De facto matters',
      'Consent orders',
    ],
    beforeYouAct: [
      {
        title: 'Before you agree anything informally',
        body: "Informal agreements often aren't protected. We help you make arrangements that hold up.",
      },
      {
        title: 'Before you move out or make decisions',
        body: 'Early advice helps protect your position on parenting and property.',
      },
      {
        title: 'Before signing a financial agreement',
        body: 'These require independent legal advice — we make sure you understand it fully.',
      },
    ],
    howWeHelp: [
      'We explain your rights and realistic options in plain language.',
      'We focus on resolution through negotiation and mediation where possible.',
      'We protect your interests around parenting and property.',
      "We formalise agreements so they're clear and enforceable.",
      'We handle sensitive matters with care and discretion.',
    ],
    process: [
      {
        title: 'Understand your situation',
        body: 'We listen and clarify your priorities and concerns.',
      },
      {
        title: 'Explain your options',
        body: 'We set out realistic pathways for parenting and property.',
      },
      {
        title: 'Agree an approach',
        body: 'We choose a strategy that suits your circumstances and goals.',
      },
      {
        title: 'Negotiate or mediate',
        body: 'We work toward a fair, workable resolution.',
      },
      {
        title: 'Formalise the outcome',
        body: 'We document agreements or orders to protect you.',
      },
    ],
    team: [
      {
        name: 'Family Team',
        role: 'Family Law',
        blurb:
          'Family lawyers who combine legal skill with genuine care and discretion.',
      },
    ],
    faqs: [
      {
        q: 'Do we have to go to court?',
        a: 'Usually not. Most family matters are resolved through negotiation, mediation or consent orders. Court is a last resort.',
      },
      {
        q: 'How is property divided?',
        a: 'WA property settlement considers contributions and future needs. We explain how the law applies to your specific circumstances.',
      },
      {
        q: 'What is a binding financial agreement?',
        a: "It's an agreement setting out how assets are dealt with. Each party must receive independent legal advice for it to be valid — which we provide.",
      },
    ],
    guides: ['Family Law Separation First Steps Guide'],
    related: [
      { label: 'Wills, Estates & Succession', slug: 'wills-estates' },
      { label: 'Property Law', slug: 'property-law' },
    ],
    localNote:
      'We assist families across Perth and WA, with experience in the local family law courts and processes.',
  },

  'wills-estates': {
    slug: 'wills-estates',
    h1: 'Wills, Estates & Succession Lawyers Perth',
    metaLead:
      'Protect your family, your assets and your wishes — wills, EPAs, EPGs, estate planning, probate and business succession in WA.',
    heroImage:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Plan your estate',
    forWho:
      'This service is for anyone who wants to protect their estate and family, plan business succession, or administer an estate as an executor. We make estate planning clear and manageable.',
    situations: [
      'Wills & estate planning',
      'Enduring Power of Attorney (EPA)',
      'Enduring Power of Guardianship (EPG)',
      'Testamentary trusts',
      'Probate & estate administration',
      'Business succession',
      'Superannuation & death benefits',
      'Estate disputes',
    ],
    beforeYouAct: [
      {
        title: "Before it's too late",
        body: 'An out-of-date or missing will can cause disputes and delay. Planning now protects your family later.',
      },
      {
        title: 'Before appointing attorneys',
        body: "Choose and document your EPA and EPG carefully — they matter most when you can't act yourself.",
      },
      {
        title: 'Before a business handover',
        body: 'Business succession needs to align with your estate plan to avoid conflict.',
      },
    ],
    howWeHelp: [
      'We prepare clear, valid wills tailored to your family and assets.',
      'We put EPAs and EPGs in place so your wishes are respected.',
      'We advise on testamentary trusts and asset protection.',
      'We guide executors through probate and estate administration.',
      'We align business succession with your overall estate plan.',
    ],
    process: [
      {
        title: 'Understand your circumstances',
        body: 'We map your family, assets, business and wishes.',
      },
      {
        title: 'Identify risks & goals',
        body: 'We highlight gaps and what you want to protect.',
      },
      {
        title: 'Recommend a plan',
        body: 'We propose the right documents and structures.',
      },
      {
        title: 'Prepare documents',
        body: 'We draft and finalise your will, EPA, EPG and trusts.',
      },
      {
        title: 'Support administration',
        body: 'When needed, we help executors carry out the estate.',
      },
    ],
    team: [
      {
        name: 'Estates Team',
        role: 'Wills, Estates & Succession',
        blurb:
          'Estate planning lawyers who make a complex area feel clear and manageable.',
      },
    ],
    faqs: [
      {
        q: 'What happens if I die without a will?',
        a: 'Your estate is distributed under WA intestacy rules, which may not reflect your wishes and can cause delay and disputes. A valid will avoids this.',
      },
      {
        q: 'What are EPA and EPG?',
        a: 'An Enduring Power of Attorney covers financial and property decisions; an Enduring Power of Guardianship covers personal and lifestyle decisions if you lose capacity.',
      },
      {
        q: "I'm an executor — can you help with probate?",
        a: 'Yes. We guide executors through the probate application and estate administration process from start to finish.',
      },
      {
        q: 'How does business succession fit in?',
        a: 'We align your business succession plan with your estate plan so ownership and control pass smoothly and without conflict.',
      },
    ],
    guides: [
      'WA Estate Planning Checklist',
      'Business Succession Planning Guide',
    ],
    related: [
      { label: 'Family Law', slug: 'family-law' },
      { label: 'Commercial & Business Law', slug: 'commercial-business-law' },
      { label: 'Dispute Resolution & Litigation', slug: 'dispute-resolution' },
    ],
    localNote:
      'We help WA families and business owners plan and administer estates, with knowledge of the Supreme Court of WA probate process.',
  },

  franchising: {
    slug: 'franchising',
    h1: 'Franchising Lawyers Perth',
    metaLead:
      'Franchising legal advice for franchisors and franchisees across WA — disclosure, agreements and obligations before, during and after signing.',
    heroImage:
      'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Get franchising advice',
    forWho:
      "This service is for prospective and existing franchisees, and for franchisors establishing or managing a network. We make sure you understand exactly what you're committing to under the Franchising Code.",
    situations: [
      'Franchise agreement review',
      'Disclosure document review',
      'Franchising Code compliance',
      'Setting up a franchise system',
      'Franchisor obligations',
      'Renewal & exit',
      'Franchise disputes',
      'Lease alongside a franchise',
    ],
    beforeYouAct: [
      {
        title: 'Before you sign as a franchisee',
        body: 'Have the agreement and disclosure document reviewed — franchise terms are rarely negotiable but must be understood.',
      },
      {
        title: 'Before you franchise your business',
        body: 'Set up compliant documents and processes from the start to avoid Code breaches.',
      },
      {
        title: 'Before renewal or exit',
        body: 'Understand your rights and obligations well ahead of key dates.',
      },
    ],
    howWeHelp: [
      'We review franchise agreements and disclosure documents in plain English.',
      'We explain your obligations under the Franchising Code.',
      'We help franchisors establish compliant franchise systems.',
      'We advise on renewal, transfer and exit.',
      'We assist with franchise disputes proportionately.',
    ],
    process: [
      {
        title: 'Understand your role',
        body: "We clarify whether you're a franchisee or franchisor and your goal.",
      },
      {
        title: 'Review the documents',
        body: 'We examine the agreement, disclosure document and any lease.',
      },
      {
        title: 'Identify the risks',
        body: 'We flag the obligations and commercial risks that matter.',
      },
      {
        title: 'Explain your options',
        body: 'You receive clear, practical recommendations.',
      },
      {
        title: 'Act or finalise',
        body: 'We advise, negotiate where possible, or finalise the arrangement.',
      },
    ],
    team: [
      {
        name: 'Franchising Team',
        role: 'Franchising Law',
        blurb:
          'Lawyers experienced with the Franchising Code and both sides of franchise relationships.',
      },
    ],
    faqs: [
      {
        q: 'Can franchise agreements be negotiated?',
        a: 'Terms are often standardised, but understanding them fully is essential — and some points can be clarified or negotiated. We tell you where you stand.',
      },
      {
        q: 'What is a disclosure document?',
        a: "It's a document a franchisor must provide under the Franchising Code, setting out key information about the franchise. We review it and explain what it means for you.",
      },
      {
        q: 'I want to franchise my business — can you help?',
        a: 'Yes. We help franchisors establish compliant franchise agreements, disclosure documents and systems.',
      },
    ],
    guides: ['Franchise Buyer Due Diligence Checklist'],
    related: [
      { label: 'Commercial & Business Law', slug: 'commercial-business-law' },
      { label: 'Property Law', slug: 'property-law' },
      { label: 'Dispute Resolution & Litigation', slug: 'dispute-resolution' },
    ],
    localNote:
      'We advise franchisors and franchisees operating across Perth and WA, with practical knowledge of local commercial leasing that often sits alongside franchises.',
  },

  employment: {
    slug: 'employment',
    h1: 'Employment & Workplace Lawyers Perth',
    metaLead:
      'Employment and workplace relations advice for WA employers and employees — contracts, disputes, restraints, safety and compliance.',
    heroImage:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80',
    ctaLabel: 'Get workplace advice',
    forWho:
      'This service is for employers who need workplace matters handled correctly, and for employees facing a workplace issue. We give practical advice that reduces risk and resolves problems.',
    situations: [
      'Employment contracts',
      'Workplace disputes',
      'Termination & redundancy',
      'Restraints of trade',
      'Workplace safety & compliance',
      'Policies & procedures',
      'Unfair dismissal',
      'Workplace investigations',
    ],
    beforeYouAct: [
      {
        title: 'Before you hire or terminate',
        body: 'Get the contract or process right first — errors here are expensive to fix later.',
      },
      {
        title: 'Before you sign a restraint',
        body: 'Understand what a restraint of trade actually limits before you agree to it.',
      },
      {
        title: 'Before a dispute escalates',
        body: 'Early, correct handling of a workplace issue often prevents a formal claim.',
      },
    ],
    howWeHelp: [
      'We prepare and review employment contracts and workplace policies.',
      'We advise employers on termination, redundancy and compliance.',
      'We advise employees on their rights and options.',
      'We handle workplace disputes and investigations.',
      'We advise on restraints, safety and compliance obligations.',
    ],
    process: [
      {
        title: 'Understand the issue',
        body: 'We clarify the workplace situation and your objective.',
      },
      {
        title: 'Review the documents',
        body: 'We examine contracts, policies and correspondence.',
      },
      {
        title: 'Identify the risks',
        body: 'We flag the legal exposure and obligations that apply.',
      },
      {
        title: 'Advise on options',
        body: 'You get clear, practical recommendations.',
      },
      {
        title: 'Act or resolve',
        body: 'We document, advise or resolve the matter appropriately.',
      },
    ],
    team: [
      {
        name: 'Workplace Team',
        role: 'Employment & Workplace Relations',
        blurb:
          'Workplace lawyers acting for both employers and employees across WA.',
      },
    ],
    faqs: [
      {
        q: 'Do you act for employers or employees?',
        a: 'Both. We advise employers on getting workplace matters right, and employees on their rights and options.',
      },
      {
        q: 'Is a restraint of trade enforceable?',
        a: "It depends on how it's drafted and the circumstances. We assess whether a restraint is reasonable and enforceable in your situation.",
      },
      {
        q: 'Can you help with an unfair dismissal claim?',
        a: 'Yes. We advise on unfair dismissal from both sides and can help manage or respond to a claim.',
      },
    ],
    guides: ['WA Business Owner Legal Health Check'],
    related: [
      { label: 'Commercial & Business Law', slug: 'commercial-business-law' },
      { label: 'Dispute Resolution & Litigation', slug: 'dispute-resolution' },
    ],
    localNote:
      'We advise WA employers and employees, with knowledge of the state and federal workplace systems that apply in Western Australia.',
  },
}
